import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  useTheme,
  DialogTitle,
  Checkbox,
  TableBody,
  Table,
  TableContainer,
  TableCell,
  TableRow,
  TableHead,
} from "@mui/material";
import CustomTypography from "../../../components/common/form/CustomTypography";
import AppButton from "../../../components/common/buttons/AppButton";
import CloseIcon from "@mui/icons-material/Close";
import CustomTextField from "../../../components/common/textFields/TextField";
import { ProductData } from "../../../../models/ProjectManagement/ProjectPackagingSwagpacks";
import { useNotification } from "../../../../utils/NotificationsContext";

interface SelectProductPopupProps {
  open: boolean;
  products: ProductData[] | [];
  selectedProductIds?: number[];
  onClose: () => void;
  onClearAll: () => void;
  onConfirm: (selectedProducts: number[]) => void;
  mode?: "pack" | "bulk";
}

const SelectProductPopup: React.FC<SelectProductPopupProps> = ({
  open,
  products,
  selectedProductIds = [],
  onClose,
  onClearAll,
  onConfirm,
  mode = "pack",
}) => {
  const theme = useTheme();
  const { showError } = useNotification();
  const [selectedProducts, setSelectedProducts] = useState<number[]>([]);
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    if (!open) {
      setSelectedProducts([]);
      setSearchText("");
    } else if (selectedProductIds && selectedProductIds.length > 0) {
      setSelectedProducts(selectedProductIds);
    } else {
      setSelectedProducts([]);
    }
  }, [open, selectedProductIds]);

  const filteredProducts = products
    .filter((product) =>
      product.name?.toLowerCase()?.includes(searchText.toLowerCase())
    )
    .sort((a, b) => {
      const qtyA = a.quantity || 0;
      const qtyB = b.quantity || 0;
      if (qtyB !== qtyA) {
        return qtyB - qtyA;
      }
      return (a.name || "").localeCompare(b.name || "");
    });

  const handleCheckboxToggle = (productId: number) => {
    if (mode === "bulk") {
      setSelectedProducts((prev) =>
        prev.includes(productId) ? [] : [productId]
      );
    } else {
      setSelectedProducts((prev) =>
        prev.includes(productId)
          ? prev.filter((id) => id !== productId)
          : [...prev, productId]
      );
    }
  };

  const handleClearAll = () => {
    setSelectedProducts([]);
    onClearAll();
  };

  const handleSelectAll = () => {
    const visibleProductIds = filteredProducts.map((p) => p.productId);
    const allVisibleSelected = visibleProductIds.every((id) =>
      selectedProducts.includes(id)
    );
    setSelectedProducts(allVisibleSelected ? [] : visibleProductIds);
  };
  const handleConfirm = () => {
    if (selectedProducts.length === 0) {
      showError(
        "Validation Error",
        mode === "bulk"
          ? "Please select at least 1 product for bulk creation.."
          : "Please select at least 2 products to create a swagpack."
      );
      return;
    }

    if (mode === "pack" && selectedProducts.length < 2) {
      showError(
        "Validation Error",
        "Please select at least 2 products to create a swagpack."
      );
      return;
    }

    const productsWithZeroQuantity = selectedProducts.filter((productId) => {
      const product = products.find((p) => p.productId === productId);
      return product && (product.quantity || 0) === 0;
    });

    if (productsWithZeroQuantity.length > 0) {
      const productNames = productsWithZeroQuantity
        .map((productId) => {
          const product = products.find((p) => p.productId === productId);
          return product?.name || `Product ID: ${productId}`;
        })
        .join(", ");

      showError(
        "Validation Error",
        `Cannot select products with 0 available quantity: ${productNames}. Please select products with available stock.`
      );
      return;
    }

    onConfirm(selectedProducts);
  };

  const allSelected =
    filteredProducts.length > 0 &&
    filteredProducts.every((product) =>
      selectedProducts.includes(product.productId)
    );
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{
        "@media (max-width:600px)": { width: "100vw", p: 0 },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: "blur(5px)",
            backgroundColor: "rgba(0, 0, 0, 0.4)",
          },
        },
        paper: {
          sx: {
            maxHeight: "90vh", // Adaptive to content, scrolls if needed
            display: "flex",
            flexDirection: "column",
            m: 0,
            borderRadius: 2,
            p: 2,
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 3,
          pb: 2,
          pl: 5,
          pt: 2,
        }}
      >
        <CustomTypography variant="heading4">Select Products</CustomTypography>
        <IconButton aria-label="close" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          paddingLeft: 5,
          paddingRight: 5,
          overflowY: "auto",
          flex: 1,
          scrollbarWidth: "none",
        }}
      >
        <Box mb={1}>
          <CustomTextField
            value={searchText}
            onChange={(e: any) => setSearchText(e.target.value)}
            width={"100%"}
            placeholder="Search"
            customStyles={{
              backgroundColor: theme.palette.customColors.grayBackGroundColor,
            }}
          />
        </Box>

        <Box sx={{ paddingTop: 1 }}>
          <TableContainer sx={{ minHeight: "200px", scrollbarWidth: "none" }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {filteredProducts.length > 0 && mode === "pack" && (
                    <TableCell padding="checkbox" sx={{ width: "48px" }}>
                      <Checkbox
                        sx={{
                          borderRadius: "4px",
                          color: "rgba(139, 139, 139, 1)",
                          "& .MuiSvgIcon-root": { fontSize: 25 },
                        }}
                        onClick={handleSelectAll}
                        checked={allSelected}
                      />
                    </TableCell>
                  )}

                  <TableCell
                    colSpan={
                      filteredProducts.length === 0 || mode !== "pack" ? 2 : 1
                    }
                  >
                    <CustomTypography
                      variant="heading8"
                      style={{
                        color: theme.palette.customColors.greyTextColor,
                        fontWeight: "700",
                      }}
                    >
                      PRODUCT
                    </CustomTypography>
                  </TableCell>
                  <TableCell align="right">
                    <CustomTypography
                      variant="heading8"
                      style={{
                        color: theme.palette.customColors.greyTextColor,
                        fontWeight: "700",
                      }}
                    >
                      AVL QTY
                    </CustomTypography>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredProducts.map((product, index) => {
                  const isChecked = selectedProducts?.includes(
                    product.productId
                  );
                  const hasZeroQuantity = (product.quantity || 0) === 0;
                  return (
                    <TableRow key={index}>
                      <TableCell
                        padding="checkbox"
                        sx={{ borderBottom: "none !important" }}
                      >
                        <Checkbox
                          checked={isChecked}
                          onClick={() =>
                            handleCheckboxToggle(product.productId)
                          }
                        />
                      </TableCell>
                      <TableCell sx={{ borderBottom: "none !important" }}>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 2 }}
                        >
                          <Box
                            sx={{
                              height: "3.0rem",
                              width: "3.0rem",
                              backgroundColor:
                                theme.palette.customColors.grayBackGroundColor,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              borderRadius: "6px",
                            }}
                          >
                            <img
                              src={product.image_url}
                              alt="Product"
                              style={{
                                height: "2.575rem",
                                width: "2.575rem",
                                borderRadius: "6px",
                                objectFit: "cover",
                              }}
                            />
                          </Box>
                          <Box>
                            <CustomTypography
                              variant="heading8"
                              style={{ paddingBottom: 7 }}
                            >
                              {product.name}
                            </CustomTypography>
                            <CustomTypography
                              variant="body3"
                              color={theme.palette.customColors.greyTextColor}
                            >
                              Product ID: {product.productId}
                            </CustomTypography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          borderBottom: "none !important",
                          paddingBottom: "40px",
                        }}
                      >
                        <CustomTypography variant="heading8">
                          {product.quantity}{" "}
                          <span
                            style={{
                              color: theme.palette.customColors.greyTextColor,
                              fontWeight: 400,
                              fontSize: "0.675rem",
                            }}
                          >
                            pcs
                          </span>
                        </CustomTypography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {filteredProducts.length === 0 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  paddingTop: 5,
                }}
              >
                <CustomTypography
                  color={theme.palette.customColors.greyTextColor}
                  variant="body2"
                  fontWeight={700}
                >
                  NO PRODUCT FOUND!
                </CustomTypography>
              </Box>
            )}
          </TableContainer>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          paddingTop: 3,
          paddingBottom: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "20px",
          flexShrink: 0,
          flexWrap: {
            xs: "wrap",
            sm: "nowrap",
          },
        }}
      >
        <AppButton
          onClick={handleClearAll}
          label="Clear All"
          customStyles={{
            height: "2.3rem",
            minWidth: "10.438rem",
            backgroundColor: theme.palette.common.white,
            color: theme.palette.customColors.greyTextColor,
          }}
          variant="grayscale"
        />
        <AppButton
          label="Confirm Products"
          onClick={handleConfirm}
          customStyles={{
            height: "2.3rem",
            minWidth: "10.438rem",
            backgroundColor: theme.palette.customColors.purple,
            color: theme.palette.customColors.white,
          }}
          variant="primary"
        />
      </DialogActions>
    </Dialog>
  );
};

export default SelectProductPopup;
